import React, { useEffect, useState, useMemo, Fragment } from 'react'
import injectSheet from 'react-jss'
import styles, {
  groupSelectorStyles,
  groupsFilterStyles
} from './pupil-selector.styles'
import Spinner from 'pobble-components/dist/spinner'
import { useMe } from 'pobble-components/dist/pobble-user'
import { apiGetSchool } from '../../common/api/school'
import { errorHandler } from 'pobble-components/dist/error-handler'
import { Table, TableRow, HeadCell, Cell } from 'pobble-components/dist/table'
import AgeGroup from 'pobble-components/dist/age-group'
import FormFile from 'pobble-components/dist/form-elements/form-file'
import Button from 'pobble-components/dist/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpload } from '@fortawesome/pro-solid-svg-icons/faUpload'
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch'
import { PobbleAvatar } from 'pobble-components/dist/pobble-avatar'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import orderBy from 'lodash/orderBy'
import {
  GroupsSelectionProvider,
  GroupsSelection
} from 'pobble-components/dist/groups-selection'
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter
} from 'pobble-components/dist/modal'
import CircleStatus from 'pobble-components/dist/circle-status'
import NoItemsPlaceholder from 'pobble-components/dist/no-items-placeholder'
import { URLS } from 'pobble-components/dist/setup'
import Tooltip from 'pobble-components/dist/tooltip'

const GroupsSelector = injectSheet(groupSelectorStyles)(
  ({ classes, onFilter, requestClose, selectedGroups }) => {
    const { me } = useMe()

    const school = me.account.teacher_type.school

    const [data, setData] = useState(null)

    useEffect(
      () => {
        apiGetSchool(school.id, {
          include:
            'school.groups,school.group.names,school.group.visibility,school.group.counters,school.group.category,school.group_categories'
        })
          .catch(errorHandler)
          .then(res => setData(res.data.school))
      },
      [school.id]
    )

    return (
      <GroupsSelectionProvider
        selected={selectedGroups}
        groups={data && data.groups}
        categories={data && data.group_categories}>
        {store => (
          <>
            <ModalContent legacy className={classes.content}>
              {data && <GroupsSelection />}
              {!data && (
                <div className={classes.pending}>
                  <Spinner />
                </div>
              )}
            </ModalContent>
            <ModalFooter alignRight childrenGap>
              <Button tall simple action={requestClose}>
                Cancel
              </Button>
              <Button
                tall
                action={() => {
                  requestClose()
                  onFilter(store.selectedGroups)
                }}>
                Select groups
              </Button>
            </ModalFooter>
          </>
        )}
      </GroupsSelectionProvider>
    )
  }
)

const GroupsFilter = injectSheet(groupsFilterStyles)(
  ({ selectedGroups, onFilter, classes }) => {
    const [modalOpen, setModalOpen] = useState(false)

    const selectedGroupsLength = Object.keys(selectedGroups).length

    return (
      <>
        <div className={classes.container}>
          {!selectedGroupsLength && (
            <Button action={() => setModalOpen(true)} simple bleed>
              Select groups
            </Button>
          )}
          {!!selectedGroupsLength && (
            <ul className={classes.groupsInfo}>
              <li>
                <strong>
                  {selectedGroupsLength} group
                  {selectedGroupsLength > 1 && 's'} selected
                </strong>
              </li>
              <li>
                <Button simple bleed action={() => setModalOpen(true)}>
                  Edit
                </Button>
              </li>
              <li>
                <Button simple bleed action={() => onFilter({})}>
                  Reset
                </Button>
              </li>
            </ul>
          )}
        </div>
        <Modal
          isOpen={modalOpen}
          closeButton
          requestClose={() => setModalOpen(false)}>
          <ModalHeader>Select groups</ModalHeader>
          <GroupsSelector
            selectedGroups={selectedGroups}
            requestClose={() => setModalOpen(false)}
            onFilter={onFilter}
          />
        </Modal>
      </>
    )
  }
)

const PupilSelector = ({ classes, onUpload }) => {
  const { me } = useMe()
  const school = me.account.teacher_type.school
  const [pupils, setPupils] = useState(null)
  const [searchString, setSearchString] = useState('')
  const [sort, setSort] = useState({ key: 'name', direction: 'asc' })
  const [selectedGroups, setSelectedGroups] = useState({})

  useEffect(
    () => {
      apiGetSchool(school.id, {
        include:
          'school.student_types,school.student_type.account.names,school.student_type.year_group.order,school.student_type.year_group.names,school.student_type.groups,school.student_type.group.names,school.student_type.account.avatar,school.student_type.policy'
      })
        .catch(errorHandler)
        .then(res => setPupils(res.data.school.student_types))
    },
    [school.id]
  )

  const pupilsFilteredByGroups = useMemo(
    () => {
      if (!pupils) return null
      const groupIds = Object.keys(selectedGroups)
      if (!groupIds.length) return pupils
      return pupils.filter(pupil => {
        return !!pupil.groups.filter(group => selectedGroups[group.id]).length
      })
    },
    [pupils, selectedGroups]
  )

  const pupilsFilteredBySearch = useMemo(
    () => {
      if (!pupilsFilteredByGroups) return null
      return pupilsFilteredByGroups.filter(
        pupil =>
          pupil.account.full_name
            .toLowerCase()
            .indexOf(searchString.toLowerCase()) !== -1
      )
    },
    [pupilsFilteredByGroups, searchString]
  )

  const sortedPupils = useMemo(
    () => {
      if (!pupilsFilteredBySearch) return null
      return sort.key === 'name'
        ? orderBy(pupilsFilteredBySearch, pupil => pupil.account.full_name, [
            sort.direction
          ])
        : sort.key === 'age'
          ? orderBy(pupilsFilteredBySearch, pupil => pupil.year_group.order, [
              sort.direction
            ])
          : pupilsFilteredBySearch
    },
    [pupilsFilteredBySearch, sort]
  )

  const resultingPupils = sortedPupils

  return (
    <div className={classes.container}>
      <div className={classes.filters}>
        <GroupsFilter
          onFilter={setSelectedGroups}
          selectedGroups={selectedGroups}
        />
        <div className={classes.search}>
          <FormInput
            value={searchString}
            onChange={e => setSearchString(e.target.value)}
            id="search"
            placeholder="Type a pupil's name"
          />
          <label htmlFor="search">
            <Button>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </label>
        </div>
      </div>
      <div className={classes.pupils}>
        {!pupils && (
          <div className={classes.pending}>
            <Spinner />
          </div>
        )}
        {!!pupils &&
          !resultingPupils.length && (
            <NoItemsPlaceholder className={classes.noItems}>
              {!!pupils.length ? (
                <Fragment>
                  <p>
                    We searched everywhere
                    <br />
                    There are no pupils matching this criteria
                  </p>
                  <Button
                    tall
                    action={() => {
                      setSelectedGroups({})
                      setSearchString('')
                    }}
                    simple>
                    Clear filters
                  </Button>
                </Fragment>
              ) : (
                <Fragment>
                  <p>You don’t have any pupils in your school yet</p>
                  <Button simple tall href={`${URLS.DASHBOARD}/add-pupils`}>
                    Add pupils
                  </Button>
                </Fragment>
              )}
            </NoItemsPlaceholder>
          )}
        {!!pupils &&
          !!resultingPupils.length && (
            <Table className={classes.table}>
              <thead>
                <TableRow>
                  <HeadCell
                    left
                    ordered={sort.key === 'name' && sort.direction}
                    order={direction => setSort({ key: 'name', direction })}>
                    Name
                  </HeadCell>
                  <HeadCell
                    className={classes.yearHCell}
                    ordered={sort.key === 'age' && sort.direction}
                    order={direction => setSort({ key: 'age', direction })}>
                    Year
                  </HeadCell>
                  <HeadCell className={classes.permissionHCell}>
                    Permission
                  </HeadCell>
                  <HeadCell />
                </TableRow>
              </thead>
              <tbody>
                {resultingPupils.map(pupil => {
                  return (
                    <TableRow key={pupil.account.id}>
                      <Cell>
                        <div className={classes.pupilName}>
                          <PobbleAvatar
                            className={classes.avatar}
                            avatar={pupil.account.avatar}
                            name={pupil.account.full_name}
                          />
                          {pupil.account.full_name}
                        </div>
                      </Cell>
                      <Cell center className={classes.yearGroupCell}>
                        <AgeGroup data={pupil.year_group} />
                      </Cell>
                      <Cell center>
                        <CircleStatus status={pupil.policy.can_publish} />
                      </Cell>
                      <Cell right>
                        {!pupil.year_group ? (
                          <Tooltip content="Please provide the age group for this pupil to be able to upload work">
                            <Button
                              disabled
                              className={classes.uploadButton}
                              span
                              tall
                              secondary
                              icon={<FontAwesomeIcon icon={faUpload} />}>
                              <span>Upload</span>
                            </Button>
                          </Tooltip>
                        ) : (
                          <FormFile
                            multiple
                            onChange={files => {
                              onUpload({
                                files,
                                pupilId: pupil.account.id
                              })
                            }}
                            accept="image/jpeg, image/png, .pdf">
                            <Button
                              className={classes.uploadButton}
                              span
                              tall
                              secondary
                              icon={<FontAwesomeIcon icon={faUpload} />}>
                              <span>Upload</span>
                            </Button>
                          </FormFile>
                        )}
                      </Cell>
                    </TableRow>
                  )
                })}
              </tbody>
            </Table>
          )}
      </div>
    </div>
  )
}

export default injectSheet(styles)(PupilSelector)
